import React, { useMemo } from 'react';

import { useGetGmvTargetOverviewQuery } from '@/features/gmv-target-banner/api/gmvTargetApi';
import Countdown from '@/features/gmv-target-banner/components/Countdown';
import {
	DEFAULT_TIME_ZONE,
	EMPTY_VALUE,
	formatMoney,
	formatPercent,
	formatRatio,
	formatSnapshot,
	getFallbackDeadline,
	isNegative,
	isPositive,
	parseDeadline
} from '@/features/gmv-target-banner/utils/format';

// Financial values only change after a verified data update, so a slow refresh is enough.
const REFRESH_INTERVAL_MS = 15 * 60 * 1000;

const Metric = ({ label, value, highlight, children }) => (
	<div className="gmv-banner__metric">
		<p className="gmv-banner__label">{label}</p>
		<p className={`gmv-banner__value${highlight ? ' gmv-banner__value--accent' : ''}`}>{value}</p>
		{children && <div className="gmv-banner__details">{children}</div>}
	</div>
);

const getGapText = (gap) => {
	if (gap === null || gap === undefined) return null;
	if (isNegative(gap)) return `${formatMoney(Math.abs(gap))} above plan`;
	if (isPositive(gap)) return `${formatMoney(gap)} below plan`;
	return 'On plan';
};

const GmvTargetBanner = () => {
	const { data, isError } = useGetGmvTargetOverviewQuery(undefined, {
		pollingInterval: REFRESH_INTERVAL_MS
	});

	const timeZone = data?.timeZone || DEFAULT_TIME_ZONE;
	const deadline = useMemo(() => parseDeadline(data?.deadline) || getFallbackDeadline(), [data?.deadline]);

	const hasData = !!data;
	const snapshot = formatSnapshot(data?.snapshotAt, timeZone);
	const showMetrics = hasData || !isError;
	const targetLabel = hasData ? formatMoney(data.targetGmv, { trim: true }) : '';
	const isTargetReached = hasData && !isPositive(data.stillToBook);

	let paceNeeded = EMPTY_VALUE;
	if (isTargetReached) paceNeeded = 'Done';
	else if (hasData) paceNeeded = `${formatMoney(data.requiredPacePerDay)}/day`;

	return (
		<section className="gmv-banner" aria-label="GMV target overview">
			<div className="gmv-banner__top">
				<p className="gmv-banner__eyebrow">
					Nitex / The {targetLabel ? `${targetLabel} ` : `${data?.year || ''} `}year
				</p>
				{snapshot && <p className="gmv-banner__snapshot">Snapshot · {snapshot}</p>}
			</div>

			<div className="gmv-banner__grid">
				<Countdown deadline={deadline} timeZone={timeZone} />

				{showMetrics && (
					<>
						<Metric label="GMV year to date" value={formatMoney(data?.gmvYearToDate)}>
							{hasData && (
								<p>
									{formatPercent(data.percentOfTarget)} of the {targetLabel} plan
								</p>
							)}
						</Metric>

						<Metric
							label="Still to book"
							value={isTargetReached ? '$0' : formatMoney(data?.stillToBook)}
						>
							{hasData && (
								<p>{isTargetReached ? 'Target reached' : 'To book by 31 December'}</p>
							)}
						</Metric>

						<Metric
							label="If growth holds"
							value={formatMoney(data?.growthScenarioGmv)}
							highlight
						>
							{hasData && (
								<>
									<p>
										{formatPercent(data.growthPercentVsLastYear, {
											decimals: 1,
											signed: true
										})}{' '}
										vs same period last year
									</p>
									{getGapText(data.growthScenarioGapToTarget) && (
										<p>{getGapText(data.growthScenarioGapToTarget)}</p>
									)}
									<p>Flat-pace scenario: {formatMoney(data.flatPaceScenarioGmv)}</p>
								</>
							)}
						</Metric>

						<Metric label="Pace needed" value={paceNeeded}>
							{hasData && (
								<>
									{!isTargetReached && (
										<p>{formatMoney(data.requiredPacePerWeek)}/week needed</p>
									)}
									<p>
										Booked pace {formatMoney(data.bookedPacePerDay)}/day ·{' '}
										{formatMoney(data.bookedPacePerWeek)}/week
									</p>
									{!isTargetReached && (
										<p className="gmv-banner__accent-text">
											{formatRatio(data.requiredToBookedPaceRatio)} the pace booked so
											far
										</p>
									)}
								</>
							)}
						</Metric>
					</>
				)}
			</div>
		</section>
	);
};

export default GmvTargetBanner;
